/**
 * onestop-preview-data — resolves a One-Stop draft's scopes into the `data`
 * fragments the student renderer reads, for the BUILDER preview only.
 *
 * WHY THIS IS NOT SYNTHESIZED DATA, WHICH THE EARLIER DECISION RULED OUT
 * Stage 5 deliberately left the preview empty: a draft has no server-resolved
 * fragments, and inventing plausible-looking desks would have told an author the
 * page worked when nothing had been checked. That reasoning still holds for
 * INVENTED rows and is the reason none are invented here.
 *
 * What changed is that the designer now holds the real objects. An author picking
 * journeys in Setup · Configuration writes real queue, service and event ids into
 * the blocks' scopes, and the designer already has those records loaded for its
 * own lists. Resolving a scope against them is not a fake — it is the same lookup
 * the resolver does, against the same tenant data, one hop earlier.
 *
 * WHAT IS STILL HONEST ABOUT IT, AND WHAT IS NOT
 * Names, locations, durations and dates are real. Live figures are NOT, because
 * nothing client-side can know them: a queue's wait, how many people are ahead,
 * how many seats are left. Those are rendered as ABSENT (`null`) rather than
 * guessed, which is a state the renderer already draws — a desk with no wait
 * number shows its name and its open-until line and no figure. An author sees the
 * shape of their page and the objects on it, and is not shown a number that will
 * be different in production.
 *
 * A scope that resolves to nothing returns `null` rather than an empty fragment,
 * so the block takes its own empty branch and says what is missing in its own
 * words.
 */
export interface OSPreviewSource {
  /** Queues the designer has loaded, in its own summary shape. */
  queues: readonly { id: string; name?: string; locationName?: string; status?: string; openUntil?: string }[];
  /** Bookable catalogue services. */
  services: readonly { id: string; name?: string; durationMin?: number; lob?: string }[];
  /** Locations, as `UILocation` from the locations adapter — the tenant's real
   *  branch / office / boutique estate. `products` and `eventsCount` are the
   *  adapter's own derived counts, which is what makes the directory's
   *  capability chips real rather than invented. */
  locations?: readonly {
    id: string; name?: string; city?: string; active?: boolean; archived?: boolean;
    products?: readonly string[]; eventsCount?: number; staffCount?: number;
  }[];
  /** Staff, as `StaffMember` from the staff adapter. Read ONLY to answer "does this
   *  location take appointments" — the roster is the one seeded thing that ties a
   *  person to a location, so it is what makes the `books` chip real. */
  staff?: readonly { status?: string; locations?: readonly (string | { locationId?: string; locationName?: string })[] }[];
  /** Events, as `UIEvent` from the events adapter.
   *
   *  `date` and `time` on that row are DISPLAY strings — "Fri 8 May 2026",
   *  "10:00 — 12:00" — formatted in the event's own zone for a table cell. They
   *  are not machine dates. The ISO instant survives on `raw`, the untouched
   *  contract row, which is what this reads first. */
  events: readonly {
    id: string; title?: string; name?: string; location?: string;
    date?: string; time?: string; startsAt?: string; start?: string;
    raw?: { startsAt?: string; endsAt?: string; timezone?: string } | null;
  }[];
}

export interface OSPreviewBlock {
  id: string;
  type: string;
  version: number;
  config: Record<string, unknown>;
  visibility?: string;
  data?: unknown;
  /** Why the preview shows fewer rows than the author picked. Rendered by the
   *  canvas beneath the block — a silently short list is indistinguishable from
   *  a working one, which is what cost two rounds of "why can't I see my
   *  events". */
  notice?: string;
}

/** A fragment plus the reason it is shorter than the author's selection. */
interface Built { data: unknown | null; notice: string | null }

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

function missingNotice(gone: number, total: number, noun: string): string | null {
  return gone > 0 ? `${gone} of ${total} selected ${noun}${total === 1 ? "" : "s"} no longer exist${gone === 1 ? "s" : ""}.` : null;
}

const idsAt = (config: Record<string, unknown>, key: string): string[] => {
  const scope = (config?.scope ?? {}) as Record<string, unknown>;
  const raw = scope[key];
  return Array.isArray(raw) ? raw.map(String) : [];
};

const capOf = (config: Record<string, unknown>): number | null => {
  const n = config?.maxItems;
  return typeof n === "number" && n > 0 ? n : null;
};

/** `{ date, time, zone }` — the civil shape the renderer's date helpers read. */
function civil(iso: string | undefined, timeZone: string): { date: string; time: string; zone: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    zone: timeZone,
  };
}

/**
 * An event's start, from whichever shape the record carries.
 *
 * ORDER MATTERS, AND THE DISPLAY STRING IS LAST FOR A REASON.
 * The adapter's `date` field is "Fri 8 May 2026" — already formatted, already
 * localised, already rendered in the event's own zone. Parsing it back is lossy
 * and locale-dependent, so it is the last resort rather than the first read. The
 * ISO instant on `raw` is the real value and is preferred wherever it exists.
 * Reading `date` first is what made every event vanish from the preview.
 *
 * A civil `YYYY-MM-DD` is taken as-is, without a `new Date()` round trip — that
 * would reinterpret a tenant's civil date in the BROWSER's zone and can land a
 * late-evening event on the wrong day.
 */
function eventStart(
  e: {
    date?: string; time?: string; startsAt?: string; start?: string;
    raw?: { startsAt?: string; timezone?: string } | null;
  },
  timeZone: string,
): { date: string; time: string; zone: string } | null {
  if (e.date && /^\d{4}-\d{2}-\d{2}$/.test(e.date)) {
    const time = e.time && /^\d{1,2}:\d{2}/.test(e.time) ? e.time.slice(0, 5).padStart(5, "0") : "00:00";
    return { date: e.date, time, zone: timeZone };
  }
  const fromIso = civil(e.raw?.startsAt ?? e.startsAt ?? e.start, e.raw?.timezone || timeZone);
  if (fromIso) return fromIso;
  /* Last resort: the display string. `Date.parse` reads "Fri 8 May 2026" in
     every engine we target, and even a wrong-by-hours result puts the event on
     the right DAY — which is all the date tile shows. */
  return e.date && !Number.isNaN(Date.parse(e.date)) ? civil(e.date, timeZone) : null;
}

function queuesFragment(config: Record<string, unknown>, src: OSPreviewSource): Built {
  const ids = idsAt(config, "queues");
  if (ids.length === 0) return { data: null, notice: null };
  const rows = ids
    .map((id) => src.queues.find((q) => String(q.id) === id))
    .filter((q): q is NonNullable<typeof q> => !!q);
  const notice = missingNotice(ids.length - rows.length, ids.length, "queue");
  if (rows.length === 0) return { data: null, notice };
  const cap = capOf(config);
  const items = (cap ? rows.slice(0, cap) : rows).map((q) => ({
    queueId: String(q.id),
    name: q.name ?? String(q.id),
    locationName: q.locationName ?? null,
    /* Live figures are unknowable here and are absent, not zero: `0 min` is a
       claim, `null` is the renderer's "no figure" branch. */
    waitMinutes: null,
    peopleAhead: null,
    walkMinutes: null,
    busyLevel: "unknown",
    open: q.status !== "closed",
    /* Only a FULL civil value or nothing. The renderer reads `.time`, but a
       half-built `{date:"",zone:""}` is the same class of shape mismatch that
       crashed the event tile — and the designer's queue summary carries no date
       to complete it with. */
    openUntil: null,
    joinable: q.status !== "closed",
    bookable: false,
  }));
  return { data: { kind: "queues" as const, items, total: rows.length, hasMore: rows.length > items.length }, notice };
}

function appointmentsFragment(config: Record<string, unknown>, src: OSPreviewSource): Built {
  const ids = [...idsAt(config, "services"), ...idsAt(config, "categories")];
  if (ids.length === 0) return { data: null, notice: null };
  const rows = ids
    .map((id) => src.services.find((s) => String(s.id) === id))
    .filter((s): s is NonNullable<typeof s> => !!s);
  const notice = missingNotice(ids.length - rows.length, ids.length, "service");
  if (rows.length === 0) return { data: null, notice };
  const cap = capOf(config);
  const items = (cap ? rows.slice(0, cap) : rows).map((s) => ({
    serviceId: String(s.id),
    name: s.name ?? String(s.id),
    summary: s.lob ?? null,
    durationMinutes: typeof s.durationMin === "number" ? s.durationMin : null,
    locationName: null,
    staff: [] as { id: string; name: string }[],
    price: null,
    /* Availability is a live query. No openings here means the row renders its
       name and duration without a time — not a time that will not exist. */
    nextOpenings: [] as unknown[],
    nextOpeningAt: null,
  }));
  return { data: { kind: "appointments" as const, items, total: rows.length, hasMore: rows.length > items.length }, notice };
}

function eventsFragment(config: Record<string, unknown>, src: OSPreviewSource, timeZone: string): Built {
  const ids = idsAt(config, "events");
  if (ids.length === 0) return { data: null, notice: null };
  /* `start` is REQUIRED on an event item — the renderer's date tile dereferences
     it without a guard, because a portal-resolved event always has one. A draft
     can hold an event whose date has not been set yet, so those are dropped here
     rather than passed through with a null: the alternative crashed the canvas,
     and inventing a date would put a day on screen that is not the event's.
     Dropped events are simply absent from the preview; publishing is unaffected,
     since the page stores the id and the resolver reads the record. */
  const found = ids
    .map((id) => src.events.find((e) => String(e.id) === id))
    .filter((e): e is NonNullable<typeof e> => !!e);
  const rows = found
    .map((e) => ({ e, start: eventStart(e, timeZone) }))
    .filter((r): r is { e: NonNullable<typeof r.e>; start: NonNullable<typeof r.start> } => r.start !== null);

  /* Two different shortfalls, two different sentences. An id that resolves to
     nothing is a deleted event; an event with no readable start is one whose
     date has not been set. Collapsing them into "some events are missing" would
     name neither fix. */
  const missing = missingNotice(ids.length - found.length, ids.length, "event");
  const undated = found.length - rows.length;
  const notice = [
    missing,
    undated > 0 ? `${plural(undated, "selected event")} ${undated === 1 ? "has" : "have"} no date set, so ${undated === 1 ? "it is" : "they are"} not shown here. Set the date on the event itself.` : null,
  ].filter(Boolean).join(" ") || null;

  if (rows.length === 0) return { data: null, notice };
  const cap = capOf(config);
  const items = (cap ? rows.slice(0, cap) : rows).map(({ e, start }) => ({
    eventId: String(e.id),
    title: e.title ?? e.name ?? String(e.id),
    locationName: e.location ?? null,
    start,
    price: null,
    /* `state: "open"` is the only honest default: the designer knows the event
       exists and nothing about its capacity, and every count is null so no
       number is asserted. */
    registration: { state: "open" as const, registeredCount: null, capacity: null, waitlistCount: null },
  }));
  return { data: { kind: "events" as const, items, total: rows.length, hasMore: rows.length > items.length }, notice };
}

/**
 * Attach preview fragments to a draft's blocks. Blocks with no scope, or a scope
 * that resolves to nothing, are returned untouched so they take their own empty
 * branch.
 *//**
 * The directory, from the tenant's own locations.
 *
 * WHY LOCATIONS AND NOT THE ORG TREE. On the live page this fragment is resolved
 * from the typed org tree (JAI-929 node types, `parentId`/`depth`, subtree
 * bound). The designer has no node read, and only one of the three tenants even
 * has a nodes seed — so the preview builds a FLAT, one-level directory from the
 * locations the author can see in Administration › Locations. Every name and
 * count below is that tenant's real data; the only thing the preview cannot
 * show is nesting, which is why every node comes back at depth 0 with no
 * children and the notice says so.
 *
 * CAPABILITIES ARE DERIVED, NOT DECLARED. There is no offering table in the
 * designer's reach, so each chip is read off something real by CORRELATING two
 * seeded collections: `books` from the roster (an active staff member assigned to
 * that location), `walkIns` from a queue whose own location line names it,
 * `events` from the adapter's events count.
 *
 * Two earlier attempts were wrong the same way and are worth recording: `books`
 * read `location.products`, then `location.staffCount`. Both are OPTIONAL fields
 * on the location DTO that the seed path never fills — the adapter resolves them to
 * `[]` and `0` — so a bank selling nothing but appointments rendered ten branches
 * all claiming to take none. A capability has to be correlated from a collection
 * that is actually seeded, the way `walkIns` always was; a location's own summary
 * counts are derived server-side and are simply absent here (2026-09-04). A location with none of the three still renders — an author needs to
 * see the branch that offers nothing as much as the one that offers everything.
 */
function directoryFragment(config: Record<string, unknown>, src: OSPreviewSource): Built {
  const all = (src.locations ?? []).filter((l) => l.active !== false && l.archived !== true);
  if (all.length === 0) return { data: null, notice: null };

  /* A queue counts for a location when its own location line names it. The
     designer's queue summary carries `locationName`, not an id. */
  /* A location takes appointments when an active staff member is assigned to it.
     The roster's assignment carries `locationId`; older rows carry a bare name
     string, so both are matched. */
  const booksAt = (id: string, name: string) => {
    const wantId = id.trim().toLowerCase(), wantName = name.trim().toLowerCase();
    return (src.staff ?? []).some((m) => m.status !== "inactive" && (m.locations ?? []).some((a) => {
      const aid = (typeof a === "string" ? "" : String(a.locationId ?? "")).trim().toLowerCase();
      const an = (typeof a === "string" ? a : String(a.locationName ?? "")).trim().toLowerCase();
      return (aid.length > 0 && aid === wantId) || (an.length > 0 && an === wantName);
    }));
  };
  const queuesAt = (name: string) => {
    const needle = name.trim().toLowerCase();
    return needle.length > 0 && src.queues.some((q) => String(q.locationName ?? "").trim().toLowerCase() === needle);
  };

  const root = ((config?.scope ?? {}) as Record<string, unknown>).root;
  const cap = capOf(config);
  const shown = cap ? all.slice(0, cap) : all;
  const nodes = shown.map((l) => ({
    nodeId: String(l.id),
    parentId: null,
    name: String(l.name ?? l.id),
    /* `nodeType` is a free string by contract — the org model owns the vocabulary.
       The preview has no node type for a location, so it says what it knows:
       this row came from a location. */
    nodeType: "location",
    depth: 0,
    childCount: 0,
    capabilities: {
      books: booksAt(String(l.id ?? ""), String(l.name ?? "")),
      walkIns: queuesAt(String(l.name ?? "")),
      events: (l.eventsCount ?? 0) > 0,
    },
  }));

  return {
    data: { kind: "directory", root: typeof root === "string" && root.length > 0 ? root : null, nodes, total: all.length, hasMore: nodes.length < all.length },
    notice: `Preview: ${plural(nodes.length, "location")} from this tenant, one level deep, with capabilities correlated from its roster, queues and events. The live page resolves the org tree, so nesting and node types will differ.`,
  };
}

export function withPreviewData(
  blocks: readonly { uid: string; id: string; type: string; version: number; config: Record<string, unknown>; visibility?: string }[],
  src: OSPreviewSource,
  timeZone: string,
): OSPreviewBlock[] {
  return blocks.map((b) => {
    const config = b.config ?? {};
    const out: OSPreviewBlock = { id: b.id, type: b.type, version: b.version, config, visibility: b.visibility };
    /* A fragment built here is hand-shaped data handed to a renderer written
       against server-shaped data. When the two disagree the renderer throws, and
       a throw inside the canvas takes the whole DESIGNER down — an author loses
       the screen they were working on because a preview could not draw a row.
       So a failure degrades to no fragment, which is the block's own empty
       state, and says so once in the console. */
    try {
      const built: Built = b.type === "queues" ? queuesFragment(config, src)
        : b.type === "appointments" ? appointmentsFragment(config, src)
        : b.type === "events" ? eventsFragment(config, src, timeZone)
        : b.type === "directory" ? directoryFragment(config, src)
        : { data: null, notice: null };
      if (built.data) out.data = built.data;
      if (built.notice) out.notice = built.notice;
    } catch (err) {
      // eslint-disable-next-line no-console -- once, and it is a preview fault
      console.error(`[one-stop] preview data for ${b.type} "${b.id}" could not be built; showing the empty state.`, err);
    }
    return out;
  });
}
